import { JSX, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import InterestCarousel from '../../../../components/InterestCarousel/InterestCarousel';
import { useResize } from '../../../../hooks';
import { STORY_ROUTE } from '../../../../router/router';
import { getWithOutSpecialChars } from '../../../../utils';
import { IDailyLookBook } from '../../../../services/api/lookBook/lookbook-model-definitions.ts';
import StoryCoverCard from './components/StoryCoverCard/StoryCoverCard';
import { getInterestName } from '../../utils';
import StorySkeletonMain from './components/StorySkeletonMain/StorySkeletonMain';
import SingleCard from '../../components/SingleCard/SingleCard';
import { getSSRTranslation } from '@/i18n.ts';
import { CommonPageDataContext } from '../../../../ContextWrapper/WithInterest/CommonPageDataContext.ts';

import './index.scss';
import { useQuery } from '@tanstack/react-query';
import { StoryRouteResponse } from '@moment/net/classic-fotoscapes/lookbook-story-fetch-query.ts';
import { AdsLayout } from '@moment/layouts/Ads/AdsLayout.tsx';

export interface StoryProps {
  queryFn: (options: any) => Promise<StoryRouteResponse>;
}

const Story: () => JSX.Element = (props: StoryProps) => {
  const { isMobile } = useResize();

  const { interests, brandGroupId, utmSource } = useContext(
    CommonPageDataContext
  );
  const params = useParams();

  const qFn = useMemo(() => props.queryFn(params), [params]);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const fixedParamsObject = useMemo(
    () =>
      Object.keys(params)
        .filter((key) => key !== '*')
        .reduce(
          (acc, currentKey) => {
            acc[currentKey] = params[currentKey];
            return acc;
          },
          {} as Record<string, any>
        ),
    [params.id]
  );

  const { data, error, isLoading } = useQuery<
    StoryRouteResponse,
    Error,
    StoryRouteResponse
  >({
    queryKey: [fixedParamsObject, lang],
    queryFn: qFn,
    enabled: !!params.uid,
  });

  const popularList = useMemo(
    () => (data ? data.popularInterest || [] : []),
    [data]
  );
  const mayLikeList = useMemo(
    () => (data ? data.travelInterest || [] : []),
    [data]
  );
  const lookBook = useMemo(() => (data ? data.lookbook : null), [data]);

  const onStoryItemClick = useCallback(() => {
    window.scrollTo(0, 0);
  }, []);

  const showRelatedStories = false;

  const interestsMap = interests.mapped;

  if (isLoading) {
    return <StorySkeletonMain />;
  }

  return (
    <div className="story">
      {lookBook && interests ? (
        <div className="story__main">
          <AdsLayout
            brandGroupIdOverride={brandGroupId}
            utmSourceOverride={utmSource}
          >
            <StoryCoverCard
              lookBook={lookBook.lookbook}
              mayLikeList={mayLikeList}
              interestMap={interests.mapped}
            />
          </AdsLayout>
        </div>
      ) : (
        <StorySkeletonMain />
      )}

      {showRelatedStories && (
        <div className="story__related">
          <h2 className="related__title">{t('relatedStories')}</h2>

          <div className="related__gallery-wrapper">
            {isMobile ? (
              <div className="related__list">
                <div className="related__list-column">
                  {popularList.slice(0, 3).map((item) => {
                    const { title, previews, uid, owner } = item;

                    return (
                      <SingleCard
                        interestId={item.interests[0]}
                        uid={uid}
                        key={uid}
                        imgList={previews}
                        title={getSSRTranslation(title, i18n)}
                        owner={owner}
                        link={STORY_ROUTE.path.replace(':uid', uid)}
                      />
                    );
                  })}
                </div>
                <div className="related__list-column">
                  {popularList.slice(3, 6).map((item) => {
                    const { uid, title, owner, previews } = item;

                    return (
                      <SingleCard
                        interestId={item.interests[0]}
                        uid={uid}
                        key={uid}
                        imgList={previews}
                        title={getSSRTranslation(title, i18n)}
                        owner={owner}
                        link={STORY_ROUTE.path.replace(':uid', uid)}
                      />
                    );
                  })}
                </div>
              </div>
            ) : (
              <InterestCarousel
                isSmallView={true}
                isMobile={isMobile}
                itemList={popularList}
                renderItem={({
                  brandLogo,
                  summary,
                  title,
                  interests,
                  previews,
                  uid,
                }: IDailyLookBook) => {
                  const interestName = interestsMap
                    ? getInterestName(interests[0], interestsMap, i18n)
                    : '';

                  return (
                    <div onClick={onStoryItemClick}>
                      <SingleCard
                        interestId={interests[0]}
                        uid={uid}
                        title={getSSRTranslation(title, i18n)}
                        imgList={previews}
                        logoURL={brandLogo}
                        interest={interestName}
                        description={getSSRTranslation(summary, i18n)}
                        descriptionLineCount={2}
                        link={STORY_ROUTE.path
                          .replace(':uid', uid)
                          .replace(':title?', getWithOutSpecialChars(title.en))}
                      />
                    </div>
                  );
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Story;
